import { useMemo, useState, type ReactNode } from "react";

import type { MaterialDef } from "../sim/types";

import { Logo } from "./Logo";

export interface SidebarProps {
  materials: MaterialDef[];
  selectedMaterialId: number;
  onSelectMaterial: (id: number) => void;
  /** Current world grid resolution (square, in cells). */
  worldSize: number;
  /** Selectable resolutions. Picking one recreates the sandbox at
   *  that size — bigger means more, smaller cells (room for large
   *  gate circuits); the current scene is reset. */
  worldSizes: readonly number[];
  onWorldSize: (n: number) => void;
  /** Full 3D Mode: when on, a standalone first-person 3D scene
   *  (dirt ground + sun) takes over the canvas. Has nothing to do
   *  with the 2D sim — purely a "walk around" view. */
  full3DMode: boolean;
  onFull3DMode: () => void;
  brushRadius: number;
  onBrushRadius: (r: number) => void;
  brushKelvin: number;
  onBrushKelvin: (t: number) => void;
  heatMode: boolean;
  onHeatMode: (v: boolean) => void;
  /** Action tool: when on, single-click toggles the
   *  trigger-tagged cell under the cursor (e.g. on/off
   *  switches) instead of painting. Mutually exclusive with
   *  the paint / heat / eraser modes. */
  actionMode: boolean;
  onActionMode: () => void;
  /** Move tool: when on, click-and-drag pans the canvas-host scroll
   *  position (so you can navigate large worlds without the +/- zoom
   *  buttons), and double-click lifts the 4-connected same-material
   *  region under the cursor — the next single click drops it at
   *  the new location. Mutually exclusive with the paint / heat /
   *  action / code / eraser modes. */
  moveMode: boolean;
  onMoveMode: () => void;
  /** Code tool: when on, single-click on a `cpu` machine (chip or
   *  its RAM / screen) opens the Code Tool panel — the disassembled
   *  ISA + an editable assembly buffer you can load back. Mutually
   *  exclusive with the paint / heat / action / eraser modes. */
  codeMode: boolean;
  onCodeMode: () => void;
  /** Dimension Switch tool: when on, single-click on a `dimension`
   *  portal cell enters its child world (switching the viewed layer).
   *  A navigation tool — it never paints. Mutually exclusive with the
   *  paint / heat / action / code / eraser modes. */
  dimensionMode: boolean;
  onDimensionMode: () => void;
  /** Atomic Mode: pause the world, drag-select a hotspot, and run a
   *  wrapped atomify (LAMMPS molecular dynamics) over the canvas for
   *  that region. Sibling of the Dimension tool — both "change how you
   *  look at the world" rather than paint into it. */
  atomicMode: boolean;
  onAtomicMode: () => void;
  /** 3D Multi-Verse View: a paused snapshot of every layer (the main
   *  world + each spawned dimension) rendered as planes stacked in
   *  depth that you can orbit. A pure visualisation — never ticks the
   *  sim. Lives under the Dimension tool since both are about moving
   *  between worlds. */
  multiverse3D: boolean;
  onMultiverse3D: () => void;
  /** Configured ambient ("room") temperature in Kelvin. The slider
   *  re-targets every empty cell's temperature toward this value
   *  via setAmbientTemperature on the sandbox. */
  roomTemperatureK: number;
  onRoomTemperatureK: (k: number) => void;
  /** Search query shared between the materials list and the
   *  Shapes panel rendered below it. App owns the state so a
   *  single input drives both lists from one place. */
  filter: string;
  onFilterChange: (next: string) => void;
  /** Slot for extra panels (e.g. Shapes) that render below the materials. */
  children?: ReactNode;
  /** Mobile drawer state. Desktop ignores this — CSS keeps the
   * sidebar permanently visible on wide viewports. */
  isOpen: boolean;
  /** Close handler for the mobile drawer's explicit close button. */
  onClose: () => void;
}

interface CategoryGroup {
  category: string;
  members: { material: MaterialDef; index: number }[];
}

const CATEGORY_ORDER = [
  "powder",
  "liquid",
  "gas",
  "solid",
  "metal",
  "alloy",
  "mineral",
  "acid",
  "base",
  "salt",
  "biological",
  "electronic",
  "radioactive",
  "plasma",
  "special",
];

const DEFAULT_OPEN_CATEGORIES = new Set(["powder", "liquid"]);

// Categories whose materials are surfaced through the Shapes sidebar
// instead of as raw material buttons. The materials still exist in the
// engine; they just don't show up in the paint palette.
//
// `shape_internal_hidden` is the canonical name (since 2026) for
// materials that only exist to be the internals of a Shape stamp —
// switch innards, lamp innards, gate cells, microwave casing, etc.
// Previously the same set of materials carried `category: equipment`,
// which overloaded "equipment" with both "type of thing" and
// "hidden from palette"; renaming makes the visibility intent
// explicit and frees `equipment` for the Shapes-sidebar use it
// already has there.
const HIDE_FROM_PALETTE = new Set(["shape_internal_hidden"]);

function groupMaterials(
  materials: MaterialDef[],
  filter: string,
): CategoryGroup[] {
  const needle = filter.trim().toLowerCase();
  const buckets = new Map<string, { material: MaterialDef; index: number }[]>();
  materials.forEach((m, index) => {
    if (m.id === "empty") return;
    const cat =
      ((m as unknown as { category?: string }).category as string | undefined) ?? "other";
    if (HIDE_FROM_PALETTE.has(cat)) return;
    // Variants surface through their parent's dropdown rather than as
    // their own top-level palette entries — see VARIANTS.md. While
    // the user is filtering, surface them too so search still works.
    if (m.hidden_in_palette && needle.length === 0) return;
    if (needle.length > 0) {
      const haystack = `${m.id} ${m.name}`.toLowerCase();
      if (!haystack.includes(needle)) return;
    }
    const list = buckets.get(cat) ?? [];
    list.push({ material: m, index });
    buckets.set(cat, list);
  });
  const ordered: CategoryGroup[] = [];
  for (const cat of CATEGORY_ORDER) {
    const members = buckets.get(cat);
    if (members && members.length > 0) {
      ordered.push({ category: cat, members });
      buckets.delete(cat);
    }
  }
  for (const [category, members] of buckets) {
    ordered.push({ category, members });
  }
  return ordered;
}

export function Sidebar(props: SidebarProps) {
  const filter = props.filter;
  const setFilter = props.onFilterChange;
  // Which parent-material's variant chooser is currently open, by id.
  // Null when no chooser is showing. Variant chooser opens on first
  // click of a parent material; second click selects the parent.
  const [variantOpenId, setVariantOpenId] = useState<string | null>(null);
  const groups = useMemo(
    () => groupMaterials(props.materials, filter),
    [props.materials, filter],
  );
  // Quick `id -> { material, index }` lookup for resolving variant ids
  // to their indices in the parent material array.
  const byId = useMemo(() => {
    const out = new Map<string, { material: MaterialDef; index: number }>();
    props.materials.forEach((m, index) => out.set(m.id, { material: m, index }));
    return out;
  }, [props.materials]);
  const filtering = filter.trim().length > 0;
  const totalShown = useMemo(
    () => groups.reduce((n, g) => n + g.members.length, 0),
    [groups],
  );
  // Total visible (excluding `empty`, the `shape_internal_hidden`
  // category whose materials live behind Shape stamps, and variant
  // materials hidden behind
  // their parent's dropdown).
  const totalVisible = useMemo(
    () => groupMaterials(props.materials, "").reduce((n, g) => n + g.members.length, 0),
    [props.materials],
  );

  const selectAndClose = (index: number) => {
    props.onSelectMaterial(index);
    setVariantOpenId(null);
  };

  return (
    <aside className={"sidebar" + (props.isOpen ? " is-open" : "")}>
      <button
        type="button"
        className="nav-drawer-close"
        onClick={props.onClose}
        aria-label="Close materials panel"
        title="Close"
      >
        ×
      </button>
      <div className="brand">
        <Logo size={36} />
        <div className="brand-text">
          <h1>CoScience.Co</h1>
          <p className="subtitle">data-driven chemistry / physics sandbox</p>
        </div>
      </div>

      {/* Brush + Heat-tool live above the materials list. The
       *  reasoning is muscle-memory: tweaking radius / temperature
       *  is how the user calibrates the *next* paint stroke, so the
       *  controls belong on the path between "thinking about the
       *  brush" and "picking a material". On mobile this also
       *  keeps both controls visible without scrolling, so the
       *  drawer doesn't have to be reopened just to nudge the
       *  temperature. */}
      <h2>World size</h2>
      {/* Grid resolution. Bigger = more, smaller cells (the canvas
       *  keeps its on-screen size, so pixels shrink — use the
       *  magnifier to inspect and a radius-0 brush for single cells).
       *  Changing it rebuilds the world, clearing the current scene. */}
      <div className="tool-row world-size-row">
        {props.worldSizes.map((n) => (
          <button
            key={n}
            type="button"
            className={"world-size-button" + (props.worldSize === n ? " active" : "")}
            aria-pressed={props.worldSize === n}
            onClick={() => {
              if (
                n === props.worldSize ||
                // eslint-disable-next-line no-alert
                window.confirm(`Resize the world to ${n}×${n}? This clears the current scene.`)
              ) {
                props.onWorldSize(n);
              }
            }}
            title={`${n}×${n} cells`}
          >
            {n}
          </button>
        ))}
      </div>
      {/* Full 3D Mode: a standalone first-person scene (dirt + sun),
       *  unrelated to the 2D grid. Sits directly under World size so
       *  the two "what am I looking at" controls live together. */}
      <button
        type="button"
        className={"full3d-toggle" + (props.full3DMode ? " active" : "")}
        onClick={props.onFull3DMode}
        aria-pressed={props.full3DMode}
        title={
          props.full3DMode
            ? "Leave Full 3D Mode — back to the 2D sandbox"
            : "Full 3D Mode — walk around a 3D world (dirt ground + sun)"
        }
      >
        <span className="full3d-toggle-icon" aria-hidden="true">
          <svg width="18" height="18" viewBox="0 0 18 18">
            {/* Simple cube glyph to read as "3D". */}
            <path
              d="M9 1.5 16 5.25 16 12.75 9 16.5 2 12.75 2 5.25 Z"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.2"
              strokeLinejoin="round"
            />
            <path
              d="M9 1.5 9 9 M9 9 16 5.25 M9 9 2 5.25"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.2"
              strokeLinejoin="round"
            />
          </svg>
        </span>
        <span>{props.full3DMode ? "Exit Full 3D Mode" : "Full 3D Mode"}</span>
      </button>

      <h2>Brush</h2>
      <div className="tool-row">
        <label>
          Radius: {props.brushRadius.toFixed(2)}
          <input
            type="range"
            min={0}
            max={20}
            step={0.01}
            value={props.brushRadius}
            onChange={(e) => props.onBrushRadius(Number(e.target.value))}
          />
        </label>
      </div>

      <h2>Action tool</h2>
      {/* Toggling this on routes single-click on the canvas to
       *  `toggleAt(x, y)` — flips the state of any cell tagged
       *  `trigger` (e.g. on/off switches) and is a no-op on
       *  every other material. Mutually exclusive with the
       *  paint / heat / eraser tools so a click never does two
       *  things at once. The double-click shortcut on the
       *  canvas keeps working in any mode for power users. */}
      <div className="tool-row">
        <button
          type="button"
          className={"action-tool-toggle" + (props.actionMode ? " active" : "")}
          onClick={props.onActionMode}
          aria-pressed={props.actionMode}
          title={
            props.actionMode
              ? "Action tool ON — single click toggles a switch instead of painting"
              : "Action tool OFF — clicks paint normally"
          }
        >
          <span className="action-tool-icon" aria-hidden="true">
            <svg width="28" height="28" viewBox="0 0 28 28">
              {/* Cursor / pointer hand silhouette. The pointer
               *  is the universal "your click does an action,
               *  not a paint stroke" cue. */}
              <path
                d="M9 4v11l-3-2-1 2 6 7h7l3-3v-6l-1-1h-2l-1-1h-2l-1-1h-2V4z"
                fill="#cfd6f4"
                stroke="#8a8fa8"
                strokeWidth="0.8"
                strokeLinejoin="round"
              />
            </svg>
          </span>
          <span className="action-tool-label">
            <strong>Action tool {props.actionMode ? "ON" : "OFF"}</strong>
            <small>
              {props.actionMode
                ? "click toggles switches"
                : "click paints / heats"}
            </small>
          </span>
        </button>
      </div>

      <h2>Move tool</h2>
      {/* The move tool gives the canvas a "map drag" affordance:
       *  click-and-drag pans the canvas-host scroll position (handy
       *  on 2048 worlds with the new sub-1x zoom rungs), and double-
       *  click lifts the 4-connected same-material region under the
       *  cursor — the next click drops it at the new location.
       *  Mutually exclusive with the paint / heat / action / code
       *  modes so the same pointerdown can never do two things. */}
      <div className="tool-row">
        <button
          type="button"
          className={"action-tool-toggle" + (props.moveMode ? " active" : "")}
          onClick={props.onMoveMode}
          aria-pressed={props.moveMode}
          title={
            props.moveMode
              ? "Move tool ON — drag to pan; double-click to lift, click to drop"
              : "Move tool OFF — clicks paint normally"
          }
        >
          <span className="action-tool-icon" aria-hidden="true">
            <svg width="28" height="28" viewBox="0 0 28 28">
              {/* Four-headed arrow — the canonical "drag in any
               *  direction" cue. Center disc reads as the pickup
               *  handle for the double-click-to-lift gesture. */}
              <g
                fill="#cfd6f4"
                stroke="#8a8fa8"
                strokeWidth="0.8"
                strokeLinejoin="round"
              >
                <path d="M14 3 L11 7 H13 V12 H8 V10 L4 13 L8 16 V14 H13 V19 H11 L14 23 L17 19 H15 V14 H20 V16 L24 13 L20 10 V12 H15 V7 H17 Z" />
                <circle cx="14" cy="13" r="2.5" fill="#8a8fa8" />
              </g>
            </svg>
          </span>
          <span className="action-tool-label">
            <strong>Move tool {props.moveMode ? "ON" : "OFF"}</strong>
            <small>
              {props.moveMode
                ? "drag to pan · dbl-click to lift"
                : "click paints / heats"}
            </small>
          </span>
        </button>
      </div>

      <h2>Heat tool</h2>
      {/* The fire/ice toggle replaces the unobtrusive checkbox.
       *  Fire half lights up when heat-tool is on; the ice half
       *  hints that you can pick a *cold* temperature too (any
       *  value below the configured ambient). The button itself
       *  toggles the mode; the temperature slider underneath
       *  picks the value the brush will write into the field. */}
      <div className="tool-row">
        <button
          type="button"
          className={"heat-tool-toggle" + (props.heatMode ? " active" : "")}
          onClick={() => props.onHeatMode(!props.heatMode)}
          aria-pressed={props.heatMode}
          title={
            props.heatMode
              ? "Heat tool ON — brush writes temperature instead of material"
              : "Heat tool OFF — brush paints the selected material"
          }
        >
          <span className="heat-tool-icon" aria-hidden="true">
            <svg width="28" height="28" viewBox="0 0 28 28">
              {/* Left half: flame (heat). Right half: snowflake
               *  (cold). The two combine to read as "the slider
               *  below sets a temperature, hot or cold". */}
              <defs>
                <clipPath id="heat-tool-left">
                  <rect x="0" y="0" width="14" height="28" />
                </clipPath>
                <clipPath id="heat-tool-right">
                  <rect x="14" y="0" width="14" height="28" />
                </clipPath>
              </defs>
              <g clipPath="url(#heat-tool-left)">
                <path
                  d="M14 4 C 11 9 7 12 7 17 a 7 7 0 0 0 14 0 c 0 -4 -4 -7 -7 -13 z"
                  fill="#ff7a3a"
                  stroke="#ff3a00"
                  strokeWidth="0.8"
                />
                <path
                  d="M14 11 C 12 14 11 16 11 18 a 3 3 0 0 0 6 0 c 0 -2 -1 -4 -3 -7 z"
                  fill="#ffd66a"
                />
              </g>
              <g
                clipPath="url(#heat-tool-right)"
                stroke="#9ad8ff"
                strokeWidth="1.6"
                strokeLinecap="round"
                fill="none"
              >
                <line x1="14" y1="4" x2="14" y2="24" />
                <line x1="6" y1="14" x2="22" y2="14" />
                <line x1="8.5" y1="8.5" x2="19.5" y2="19.5" />
                <line x1="19.5" y1="8.5" x2="8.5" y2="19.5" />
              </g>
            </svg>
          </span>
          <span className="heat-tool-label">
            <strong>Heat tool {props.heatMode ? "ON" : "OFF"}</strong>
            <small>
              {props.heatMode
                ? "brush writes temperature"
                : "brush paints material"}
            </small>
          </span>
        </button>
      </div>
      <div className="tool-row">
        <label className={"heat-temp-slider" + (props.heatMode ? "" : " dimmed")}>
          {/* Show the picked temperature in both Kelvin (the
           *  engine's unit, what the chem db uses for thresholds
           *  like `min_temperature_k: 373.15`) and Celsius (what
           *  most users actually think in for everyday phenomena
           *  like boiling / freezing). 273.15 K = 0 °C. Round to
           *  the nearest integer so the readout doesn't jitter. */}
          <span className="tool-row-label">
            Heat-tool temperature: <strong>{Math.round(props.brushKelvin)} K</strong>
            {" "}({Math.round(props.brushKelvin - 273.15)} °C)
          </span>
          <input
            type="range"
            min={100}
            max={3000}
            value={props.brushKelvin}
            onChange={(e) => props.onBrushKelvin(Number(e.target.value))}
          />
        </label>
      </div>

      <h2>Code tool</h2>
      {/* Toggling this on routes single-click on the canvas to the
       *  Code Tool: clicking a `cpu` machine (the chip or its RAM /
       *  screen) opens a panel showing the program inside it —
       *  disassembled ISA plus an editable assembly buffer you can
       *  load back, restarting the CPU. A no-op on every non-CPU
       *  cell. Mutually exclusive with the paint / heat / action /
       *  eraser tools. */}
      <div className="tool-row">
        <button
          type="button"
          className={"action-tool-toggle" + (props.codeMode ? " active" : "")}
          onClick={props.onCodeMode}
          aria-pressed={props.codeMode}
          title={
            props.codeMode
              ? "Code tool ON — click a CPU / screen to view & edit its program"
              : "Code tool OFF — clicks paint normally"
          }
        >
          <span className="action-tool-icon" aria-hidden="true">
            <svg width="28" height="28" viewBox="0 0 28 28">
              {/* Angle brackets + slash — the universal "code" glyph. */}
              <path
                d="M10 8 L5 14 L10 20"
                fill="none"
                stroke="#9ad8ff"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M18 8 L23 14 L18 20"
                fill="none"
                stroke="#9ad8ff"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <line
                x1="15"
                y1="6"
                x2="13"
                y2="22"
                stroke="#cfd6f4"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </span>
          <span className="action-tool-label">
            <strong>Code tool {props.codeMode ? "ON" : "OFF"}</strong>
            <small>
              {props.codeMode
                ? "click a CPU to view/edit code"
                : "click paints / heats"}
            </small>
          </span>
        </button>
      </div>

      <h2>Dimension tool</h2>
      {/* Toggling this on routes single-click to the Dimension Switch
       *  tool: clicking a `dimension` portal cell enters its isolated
       *  child world (the view switches to that layer; a Back button
       *  appears over the canvas). Paint its interior, place pins, then
       *  step back out. A no-op on every non-dimension cell, and it
       *  never paints. Mutually exclusive with the other tools. */}
      <div className="tool-row">
        <button
          type="button"
          className={"action-tool-toggle" + (props.dimensionMode ? " active" : "")}
          onClick={props.onDimensionMode}
          aria-pressed={props.dimensionMode}
          title={
            props.dimensionMode
              ? "Dimension tool ON — click a dimension portal to enter it"
              : "Dimension tool OFF — clicks paint normally"
          }
        >
          <span className="action-tool-icon" aria-hidden="true">
            <svg width="28" height="28" viewBox="0 0 28 28">
              {/* Nested squares — a portal into a smaller world. */}
              <rect
                x="5"
                y="5"
                width="18"
                height="18"
                rx="2"
                fill="none"
                stroke="#b48cff"
                strokeWidth="2"
              />
              <rect
                x="10"
                y="10"
                width="8"
                height="8"
                rx="1"
                fill="none"
                stroke="#d9b0ff"
                strokeWidth="2"
              />
            </svg>
          </span>
          <span className="action-tool-label">
            <strong>Dimension tool {props.dimensionMode ? "ON" : "OFF"}</strong>
            <small>
              {props.dimensionMode
                ? "click a dimension to enter it"
                : "click paints / heats"}
            </small>
          </span>
        </button>
      </div>
      {/* Atomic Mode: pauses the sim, lets you drag-select a hotspot, then
       *  runs the wrapped atomify (molecular dynamics) over that region.
       *  Sits directly under the Dimension tool — both swap what the canvas
       *  shows rather than paint into it. */}
      <div className="tool-row">
        <button
          type="button"
          className={"action-tool-toggle" + (props.atomicMode ? " active" : "")}
          onClick={props.onAtomicMode}
          aria-pressed={props.atomicMode}
          title={
            props.atomicMode
              ? "Atomic Mode ON — drag a box to simulate that region atom-by-atom"
              : "Atomic Mode OFF — turn on to snapshot a region into molecular dynamics"
          }
        >
          <span className="action-tool-icon" aria-hidden="true">
            <svg width="28" height="28" viewBox="0 0 28 28">
              {/* A Bohr-atom glyph: nucleus + two crossed electron orbits. */}
              <circle cx="14" cy="14" r="2.4" fill="#4cd6e0" />
              <ellipse
                cx="14"
                cy="14"
                rx="10"
                ry="4"
                fill="none"
                stroke="#4cd6e0"
                strokeWidth="1.6"
              />
              <ellipse
                cx="14"
                cy="14"
                rx="10"
                ry="4"
                fill="none"
                stroke="#6fe7f0"
                strokeWidth="1.6"
                transform="rotate(60 14 14)"
              />
            </svg>
          </span>
          <span className="action-tool-label">
            <strong>Atomic Mode {props.atomicMode ? "ON" : "OFF"}</strong>
            <small>
              {props.atomicMode
                ? "drag a box → run LAMMPS"
                : "snapshot a region to atoms"}
            </small>
          </span>
        </button>
      </div>
      {/* 3D Multi-Verse View: orbit a stacked-planes snapshot of every
       *  layer. Sits directly under the Dimension tool — both are
       *  "look across worlds" controls. */}
      <button
        type="button"
        className={"full3d-toggle" + (props.multiverse3D ? " active" : "")}
        onClick={props.onMultiverse3D}
        aria-pressed={props.multiverse3D}
        title={
          props.multiverse3D
            ? "Close the 3D Multi-Verse View"
            : "3D Multi-Verse View — paused snapshot of all dimension layers, drag to orbit"
        }
      >
        <span className="full3d-toggle-icon" aria-hidden="true">
          <svg width="18" height="18" viewBox="0 0 18 18">
            {/* Stacked layers glyph to read as "the multiverse". */}
            <path
              d="M9 2 16 5.5 9 9 2 5.5 Z M2 9 9 12.5 16 9 M2 12.5 9 16 16 12.5"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.2"
              strokeLinejoin="round"
            />
          </svg>
        </span>
        <span>
          {props.multiverse3D ? "Exit 3D Multi-Verse View" : "3D Multi-Verse View"}
        </span>
      </button>

      <h2>Room temperature</h2>
      <div className="tool-row">
        <label>
          {/* Re-targets every empty cell's temperature toward this
           *  value via setAmbientTemperature; non-empty cells keep
           *  their current temperature so a torch in the middle
           *  of the scene stays hot. Range covers everyday rooms
           *  (cold storage at 0 °C up through a hot summer day at
           *  ~40 °C) — outside that, use the heat tool to drop
           *  specific cells. */}
          <span className="tool-row-label">
            Ambient: <strong>{Math.round(props.roomTemperatureK)} K</strong>
            {" "}({Math.round(props.roomTemperatureK - 273.15)} °C)
          </span>
          <input
            type="range"
            min={250}
            max={320}
            value={props.roomTemperatureK}
            onChange={(e) => props.onRoomTemperatureK(Number(e.target.value))}
          />
        </label>
      </div>

      <h2>
        Materials{" "}
        <span className="material-count">
          {filtering ? `${totalShown} / ${totalVisible}` : totalVisible}
        </span>
      </h2>
      <div className="material-search">
        <input
          type="text"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Search materials & shapes..."
          spellCheck={false}
        />
        {filtering && (
          <button
            type="button"
            className="material-search-clear"
            onClick={() => setFilter("")}
            title="Clear search"
            aria-label="Clear search"
          >
            ×
          </button>
        )}
      </div>

      {groups.length === 0 ? (
        <p className="material-empty">No materials match "{filter}".</p>
      ) : (
        groups.map((group) => (
          <details
            key={group.category}
            open={filtering || DEFAULT_OPEN_CATEGORIES.has(group.category)}
          >
            <summary className="category-summary">
              {group.category} ({group.members.length})
            </summary>
            <div className="material-grid">
              {group.members.map(({ material, index }) => {
                const swatch = material.color
                  ? `rgb(${material.color[0]}, ${material.color[1]}, ${material.color[2]})`
                  : "#888";
                const variants = (material.variants ?? [])
                  .map((id) => byId.get(id))
                  .filter((entry): entry is { material: MaterialDef; index: number } => !!entry);
                const hasVariants = variants.length > 0;
                const variantsOpen = hasVariants && variantOpenId === material.id;
                const isActive = index === props.selectedMaterialId
                  || variants.some((v) => v.index === props.selectedMaterialId);
                const onClick = () => {
                  if (!hasVariants) {
                    selectAndClose(index);
                    return;
                  }
                  // First click on a variant-bearing material opens the
                  // chooser without selecting; the user picks the exact
                  // form. Second click on the same parent locks in the
                  // parent material and closes the chooser. Click on a
                  // different parent re-targets the chooser.
                  if (variantsOpen) {
                    selectAndClose(index);
                  } else {
                    setVariantOpenId(material.id);
                  }
                };
                return (
                  <div key={material.id} className="material-cell">
                    <button
                      type="button"
                      className={
                        "material-button" +
                        (isActive ? " active" : "") +
                        (hasVariants ? " has-variants" : "")
                      }
                      onClick={onClick}
                      title={
                        hasVariants
                          ? `${material.id} (variants available)`
                          : material.id
                      }
                    >
                      <span className="swatch" style={{ background: swatch }} />
                      {material.name}
                      {hasVariants && (
                        <span className="variant-caret" aria-hidden="true">
                          ▾
                        </span>
                      )}
                    </button>
                    {variantsOpen && (
                      <div className="variant-popover" role="menu">
                        <div className="variant-popover-header">
                          {material.name} variants
                        </div>
                        <button
                          type="button"
                          role="menuitem"
                          className={
                            "variant-button" +
                            (index === props.selectedMaterialId ? " active" : "")
                          }
                          onClick={() => selectAndClose(index)}
                          title={`${material.id} (default)`}
                        >
                          <span className="swatch" style={{ background: swatch }} />
                          {material.name}
                          <span className="variant-tag">default</span>
                        </button>
                        {variants.map(({ material: vm, index: vi }) => {
                          const vSwatch = vm.color
                            ? `rgb(${vm.color[0]}, ${vm.color[1]}, ${vm.color[2]})`
                            : "#888";
                          return (
                            <button
                              key={vm.id}
                              type="button"
                              role="menuitem"
                              className={
                                "variant-button" +
                                (vi === props.selectedMaterialId ? " active" : "")
                              }
                              onClick={() => selectAndClose(vi)}
                              title={vm.id}
                            >
                              <span className="swatch" style={{ background: vSwatch }} />
                              {vm.name}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </details>
        ))
      )}

      {props.children}
    </aside>
  );
}
